import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Card,
  CardContent,
  CardHeader,
  TextField,
  MenuItem,
  CircularProgress,
  IconButton,
  Box,
} from "@mui/material";

import RefreshIcon from "@mui/icons-material/Refresh";
import TimelineIcon from "@mui/icons-material/Timeline";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SavingsIcon from "@mui/icons-material/Savings";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

const API = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000/api/";
const REVENUE_URL = `${API}admin/revenue-summary/`;

const formatMoney = (value) => {
  const n = Number(value || 0);
  return `₹${n.toLocaleString("en-IN")}`;
};

const AdminRevenueDashboard = () => {
  const [groupBy, setGroupBy] = useState("month"); // day | month | year
  const [startDate, setStartDate] = useState(""); // yyyy-mm-dd
  const [endDate, setEndDate] = useState(""); // yyyy-mm-dd

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [data, setData] = useState({
    overview: {
      invoice_count: 0,
      paid_count: 0,
      unpaid_count: 0,
      total_paid_amount: 0,
      total_unpaid_amount: 0,
      total_amount: 0,

      deposit_count: 0,
      deposit_settled_count: 0,
      deposit_collected: 0,
      deposit_settled: 0,
      total_deposit: 0, // net deposit
      total_revenue: 0,
    },
    groups: [],
  });

  const fetchRevenue = async () => {
    try {
      setLoading(true);
      setError("");

      const token = localStorage.getItem("token");

      const params = new URLSearchParams();
      params.append("mode", "library");
      if (groupBy) params.append("group_by", groupBy);
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);

      const res = await axios.get(`${REVENUE_URL}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const apiData = res.data?.data || {};
      const overview = apiData.overview || {};
      const groups = apiData.groups || [];

      setData({
        overview: {
          invoice_count: overview.invoice_count ?? 0,
          paid_count: overview.paid_count ?? 0,
          unpaid_count: overview.unpaid_count ?? 0,
          total_paid_amount: overview.total_paid_amount ?? 0,
          total_unpaid_amount: overview.total_unpaid_amount ?? 0,
          total_amount: overview.total_amount ?? 0,

          deposit_count: overview.deposit_count ?? 0,
          deposit_settled_count: overview.deposit_settled_count ?? 0,
          deposit_collected: overview.deposit_collected ?? 0,
          deposit_settled: overview.deposit_settled ?? 0,
          total_deposit: overview.total_deposit ?? 0,
          total_revenue: overview.total_revenue ?? 0,
        },
        groups,
      });
    } catch (err) {
      console.error(err);
      setError(
        err.response?.data?.detail ||
          err.response?.data?.error ||
          "Failed to load revenue data"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRevenue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupBy, startDate, endDate]);

  const overview = data?.overview || {};
  const groups = data?.groups || [];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
            <TimelineIcon className="text-green-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Revenue</h1>
            <p className="text-gray-500 text-sm">
              Paid / Unpaid / Deposits + Period wise trend
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <CircularProgress size={18} />
              Loading...
            </div>
          )}

          <IconButton
            onClick={fetchRevenue}
            disabled={loading}
            className="bg-white shadow"
            title="Refresh"
            size="large"
          >
            <RefreshIcon className={loading ? "animate-spin" : ""} />
          </IconButton>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6 shadow-lg rounded-xl">
        <CardContent>
          <div className="flex flex-wrap gap-4 items-center">
            <TextField
              select
              label="Group By"
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              size="small"
              sx={{ minWidth: 170 }}
            >
              <MenuItem value="day">Day Wise</MenuItem>
              <MenuItem value="month">Month Wise</MenuItem>
              <MenuItem value="year">Year Wise</MenuItem>
            </TextField>

            <TextField
              label="Start Date"
              type="date"
              size="small"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 190 }}
            />

            <TextField
              label="End Date"
              type="date"
              size="small"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 190 }}
            />
          </div>

          {error && (
            <div className="mt-3 text-sm text-red-600 font-medium">{error}</div>
          )}
        </CardContent>
      </Card>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        {/* Total Revenue */}
        <Card className="shadow-lg rounded-xl">
          <CardHeader
            title={<span className="text-sm text-gray-600">Total Revenue</span>}
            action={
              <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center mr-2">
                <TrendingUpIcon className="text-gray-700" />
              </div>
            }
          />
          <CardContent>
            <div className="text-2xl font-bold">
              {formatMoney(overview.total_revenue)}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Total Paid
            </div>
          </CardContent>
        </Card>

        {/* Total Paid */}
        <Card className="shadow-lg rounded-xl">
          <CardHeader
            title={<span className="text-sm text-gray-600">Total Paid</span>}
            action={
              <div className="w-9 h-9 rounded-full bg-green-50 flex items-center justify-center mr-2">
                <CheckCircleIcon className="text-green-600" />
              </div>
            }
          />
          <CardContent>
            <div className="text-2xl font-bold text-green-700">
              {formatMoney(overview.total_paid_amount)}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Paid Invoices: {overview.paid_count}
            </div>
          </CardContent>
        </Card>

        {/* Total Unpaid */}
        <Card className="shadow-lg rounded-xl">
          <CardHeader
            title={<span className="text-sm text-gray-600">Total Unpaid</span>}
            action={
              <div className="w-9 h-9 rounded-full bg-red-50 flex items-center justify-center mr-2">
                <HourglassBottomIcon className="text-red-600" />
              </div>
            }
          />
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {formatMoney(overview.total_unpaid_amount)}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Pending Invoices: {overview.unpaid_count}
            </div>
          </CardContent>
        </Card>

        {/* Total Amount */}
        <Card className="shadow-lg rounded-xl">
          <CardHeader
            title={<span className="text-sm text-gray-600">Total Amount</span>}
            action={
              <div className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center mr-2">
                <ReceiptLongIcon className="text-blue-600" />
              </div>
            }
          />
          <CardContent>
            <div className="text-2xl font-bold">
              {formatMoney(overview.total_amount)}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Total Invoices: {overview.invoice_count}
            </div>
          </CardContent>
        </Card>

        {/* Net Deposit */}
        <Card className="shadow-lg rounded-xl">
          <CardHeader
            title={<span className="text-sm text-gray-600">Net Deposit</span>}
            action={
              <div className="w-9 h-9 rounded-full bg-yellow-50 flex items-center justify-center mr-2">
                <SavingsIcon className="text-yellow-700" />
              </div>
            }
          />
          <CardContent>
            <div className="text-2xl font-bold">
              {formatMoney(overview.total_deposit)}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Deposits (Collected - Settled)
            </div>
          </CardContent>
        </Card>

        {/* Deposit Collected */}
        <Card className="shadow-lg rounded-xl">
          <CardHeader
            title={
              <span className="text-sm text-gray-600">Deposit Collected</span>
            }
            action={
              <div className="w-9 h-9 rounded-full bg-green-50 flex items-center justify-center mr-2">
                <AccountBalanceWalletIcon className="text-green-700" />
              </div>
            }
          />
          <CardContent>
            <div className="text-2xl font-bold">
              {formatMoney(overview.deposit_collected)}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Deposit Count: {overview.deposit_count}
            </div>
          </CardContent>
        </Card>

        {/* Deposit Settled */}
        <Card className="shadow-lg rounded-xl">
          <CardHeader
            title={
              <span className="text-sm text-gray-600">Deposit Settled</span>
            }
            action={
              <div className="w-9 h-9 rounded-full bg-red-50 flex items-center justify-center mr-2">
                <CancelIcon className="text-red-600" />
              </div>
            }
          />
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {formatMoney(overview.deposit_settled)}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Settled Count: {overview.deposit_settled_count}
            </div>
          </CardContent>
        </Card>

        {/* Paid / Unpaid counts */}
        <Card className="shadow-lg rounded-xl">
          <CardHeader title={<span className="text-sm text-gray-600">Counts</span>} />
          <CardContent>
            <div className="text-sm text-gray-700 flex justify-between">
              <span>Paid</span>
              <span className="font-semibold">{overview.paid_count}</span>
            </div>
            <div className="text-sm text-gray-700 flex justify-between mt-2">
              <span>Unpaid</span>
              <span className="font-semibold">{overview.unpaid_count}</span>
            </div>
            <div className="text-sm text-gray-700 flex justify-between mt-2">
              <span>Total</span>
              <span className="font-semibold">{overview.invoice_count}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Trend + Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Revenue Trend */}
        <Card className="lg:col-span-2 shadow-lg rounded-xl">
          <CardHeader title="Revenue Trend" />
          <CardContent>
            {groups?.length ? (
              <div className="space-y-3">
                {groups.map((g, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between border rounded-lg p-3 bg-gray-50"
                  >
                    <div>
                      <div className="text-sm font-medium text-gray-800">
                        {g.label || g.period || "—"}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Paid: {formatMoney(g.total_paid_amount)} • Unpaid:{" "}
                        {formatMoney(g.total_unpaid_amount)} • Net Deposit:{" "}
                        {formatMoney(g.net_deposit || 0)}
                      </div>
                    </div>

                    <div className="text-right">
                      <div className="text-sm text-gray-500">Revenue</div>
                      <div className="text-base font-semibold">
                        {formatMoney(g.total_revenue || 0)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-gray-500 text-sm">
                No trend data for selected filters.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Summary by Period */}
        <Card className="shadow-lg rounded-xl">
          <CardHeader title="Summary by Period" />
          <CardContent>
            <div className="max-h-[360px] overflow-auto">
              {groups?.length ? (
                <div className="space-y-2">
                  {groups.map((g, idx) => (
                    <div
                      key={idx}
                      className="border rounded-lg p-3 flex items-center justify-between"
                    >
                      <div className="text-sm text-gray-800">
                        {g.label || g.period || "—"}
                        <div className="text-xs text-gray-500 mt-1">
                          Invoices: {g.invoice_count} | Paid: {g.paid_count} | Unpaid:{" "}
                          {g.unpaid_count}
                        </div>
                      </div>
                      <div className="text-sm font-semibold">
                        {formatMoney(g.total_revenue || 0)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-gray-500 py-8 text-sm">
                  No grouped data.
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Box sx={{ height: 24 }} />
    </div>
  );
};

export default AdminRevenueDashboard;
